/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { authRegister } from "../funcs/authRegister.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RegisterRequest,
  RegisterSecurity,
} from "../models/operations/register.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RegisterMutationVariables = {
  request: RegisterRequest;
  security?: RegisterSecurity | undefined;
  options?: RequestOptions;
};

export type RegisterMutationData = void;

export type RegisterMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * register auth
 *
 * @remarks
 * Register a new org for a user with their session information.
 */
export function useRegisterMutation(
  options?: MutationHookOptions<
    RegisterMutationData,
    RegisterMutationError,
    RegisterMutationVariables
  >,
): UseMutationResult<
  RegisterMutationData,
  RegisterMutationError,
  RegisterMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRegisterMutation(client, options),
    ...options,
  });
}

export function mutationKeyRegister(): MutationKey {
  return ["@gram/client", "auth", "register"];
}

export function buildRegisterMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RegisterMutationVariables,
  ) => Promise<RegisterMutationData>;
} {
  return {
    mutationKey: mutationKeyRegister(),
    mutationFn: function registerMutationFn({
      request,
      security,
      options,
    }): Promise<RegisterMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(authRegister(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
